// When the page's CONTENT changes without the page reloading, and who needs to know.
//
// The homepage's spa picker replaces the page content in place rather than navigating
// (resources/js/shared/pageSwap.js, docs/homepage-location-swap.md). That breaks the
// oldest assumption in this front end - that the markup a script finds at DOMContentLoaded
// is the markup for the rest of the page's life - so this module is the replacement for
// that assumption, and every setup sweep in the site now runs through it.
//
// THE HOUSE RULE, for anyone adding front-end code after this:
//
//   - Code that FINDS elements and does something to each of them goes through
//     onContentReady(). Never a bare DOMContentLoaded listener, and never a top-level
//     querySelectorAll() at module scope. Both run exactly once, on markup that may be
//     replaced ten seconds later, and neither is given the new markup.
//   - Vue roots go through registerIsland() in ./islands.js, which is built on this.
//   - Handlers DELEGATED from the document (app.js is full of them, deliberately) need
//     nothing at all. They already work on content that arrives later, which is why they
//     were written that way.
//
// Code written to that rule works in swapped content with no further thought, whatever
// sections an editor puts on a page. That universality is the whole point: the client can
// change a homepage's sections in the CMS at any time, and no section may quietly stop
// working because of how it happened to be built.

// Fired once per outgoing region, BEFORE its markup is replaced, so a listener can tear
// down anything holding a reference into it - unmount a Vue app, close an open panel.
// detail: { root }, the element whose contents are about to go.
export const CONTENT_REPLACING = 'content:replacing';

// Fired once per swap, AFTER every region is in place. detail:
//   roots    - the region elements whose contents are new
//   document - the parsed document the content came from, so a listener can read
//              something from OUTSIDE the regions (the search bar re-seeds itself from
//              the fetched header this way, since the header is never swapped)
//   url      - the URL the content came from
//   cause    - 'navigate' (the customer changed spa) or 'popstate' (back/forward)
//
// One event for the whole swap, not one per region: a listener that mounts something
// should see the page in its finished state, not part way through being assembled.
export const CONTENT_REPLACED = 'content:replaced';

// Whether the initial document has been parsed. Module scripts (which is all of ours,
// via Vite) run before DOMContentLoaded fires, so this is normally false at import time.
function documentIsReady() {
    return document.readyState !== 'loading';
}

/**
 * Run `fn(root)` for every root of content this page has, now and forever:
 *
 *   - once for `document` when the initial markup is ready (immediately if it already
 *     is, which is the case for a module appended by the swapper itself);
 *   - once per replaced region on every swap.
 *
 * `fn` must be idempotent over elements it has already handled - it can be called again
 * with a root containing something it has seen, and the second call must be harmless.
 * Most callers get this for free by acting on a class or attribute they then set, or by
 * being the only writer of the state they set.
 *
 * @param {(root: Document|Element) => void} fn
 */
export function onContentReady(fn) {
    if (documentIsReady()) {
        fn(document);
    } else {
        document.addEventListener('DOMContentLoaded', () => fn(document), { once: true });
    }

    document.addEventListener(CONTENT_REPLACED, (event) => {
        event.detail.roots.forEach((root) => fn(root));
    });
}

/**
 * Run `fn(root)` for each region about to be replaced. For teardown only - the markup is
 * still in the document when this runs, and is gone immediately afterwards.
 *
 * @param {(root: Element) => void} fn
 */
export function onContentReplacing(fn) {
    document.addEventListener(CONTENT_REPLACING, (event) => fn(event.detail.root));
}

// The two dispatchers. Called by pageSwap.js and by nothing else - a second caller would
// mean two things claiming to own the page's content, and the teardown/setup pairing above
// only holds while one thing does.

export function announceContentReplacing(root) {
    document.dispatchEvent(new CustomEvent(CONTENT_REPLACING, { detail: { root } }));
}

export function announceContentReplaced(detail) {
    document.dispatchEvent(new CustomEvent(CONTENT_REPLACED, { detail }));
}
